import { useCallback, useEffect, useState } from "react";
import { Search, Radio } from "lucide-react";
import { api, SERVICE_TYPES, VEHICLES, whatsappInviteUrl } from "@/lib/api";
import { t } from "@/lib/i18n";
import { RideCard } from "@/components/fleet/RideCard";

const FILTERS = [{ value: "", label: "Tutte" }, ...SERVICE_TYPES.map((s) => ({ value: s.value, label: s.value }))];

const PAX = [0, 2, 4, 7, 8, 16];
const HUBS = [{ id: "airport", label: "✈️ Aeroporti" }, { id: "station", label: "🚆 Stazioni" }, { id: "national", label: "🛣️ Tratte Nazionali" }];

const HubChips = ({ hub, setHub }) => (
  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="board-hub-filters">
    {HUBS.map((h) => (
      <button key={h.id} type="button" data-testid={`filter-hub-${h.id}`} data-active={hub === h.id} onClick={() => setHub(hub === h.id ? "" : h.id)} className="ff-chip whitespace-nowrap">{t(h.label)}</button>
    ))}
  </div>
);
const BAGS = [0, 2, 4, 8, 12];

const Select = ({ testid, value, onChange, children, className = "" }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`ff-field !h-12 text-sm font-semibold ${className}`}>{children}</select>
);

const DetailFilters = ({ vehicle, onVehicle, pax, setPax, bags, setBags }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="board-detail-filters">
    <Select testid="filter-vehicle-select" value={vehicle} onChange={onVehicle} className="col-span-2 sm:col-span-1">
      <option value="">{t("Tutti i veicoli")}</option>
      {VEHICLES.map((v) => <option key={v.value} value={v.value}>{t(v.label)}</option>)}
    </Select>
    <Select testid="filter-passengers-select" value={pax} onChange={(v) => setPax(Number(v))}>
      {PAX.map((n) => <option key={n} value={n}>{n ? `${n}+ ${t("passeggeri")}` : t("Passeggeri")}</option>)}
    </Select>
    <Select testid="filter-luggage-select" value={bags} onChange={(v) => setBags(Number(v))}>
      {BAGS.map((n) => <option key={n} value={n}>{n ? `${n}+ ${t("valigie")}` : t("Valigie")}</option>)}
    </Select>
  </div>
);

const BoardFilters = ({ q, setQ, service, setService, searchRef }) => (
  <div className="flex flex-col gap-3">
    <div className="relative">
      <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
      <input
        ref={searchRef}
        data-testid="board-search-input"
        className="ff-field"
        style={{ paddingLeft: "3rem" }}
        placeholder={t("Cerca città, aeroporto, stazione…")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
    </div>
    <div className="ff-scroll-x flex gap-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      {FILTERS.map((f) => (
        <button key={f.label} type="button" data-testid={`filter-chip-${f.label.toLowerCase().replace(/\s+/g, "-")}`} data-active={service === f.value} className="ff-chip" onClick={() => setService(f.value)}>
          {t(f.label)}
        </button>
      ))}
    </div>
  </div>
);

const EmptyBoard = ({ filtered, onCreate }) => (
  <div data-testid="board-empty-state" className="ff-card mt-6 px-6 py-12 sm:py-16 flex flex-col items-center text-center gap-6 border-[#10B981]/40">
    <Radio size={40} className="text-[#10B981]" />
    <div className="flex flex-col gap-2">
      <p data-testid="board-empty-title" className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">{filtered ? t("Nessuna corsa trovata") : t("Nessuna corsa attiva")}</p>
      <p data-testid="board-empty-text" className="text-lg text-white/90">{t("Hai un viaggio o un rientro a vuoto da passare?")}</p>
    </div>
    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
      <button type="button" data-testid="board-empty-create-button" onClick={onCreate} className="ff-btn-primary h-16 px-10 uppercase text-lg w-full sm:w-auto">
        {t("PUBBLICA CORSA")}
      </button>
      <a href={whatsappInviteUrl()} target="_blank" rel="noopener noreferrer" data-testid="board-empty-invite-button" className="ff-btn-secondary h-16 px-6 uppercase text-sm w-full sm:w-auto border-[#25D366] text-[#4ADE80]">
        {t("📲 INVITA UN COLLEGA IN RETE")}
      </a>
    </div>
  </div>
);

export const LiveBoard = ({ refreshKey, searchRef, verified, vehicle, onVehicle, q, setQ, onBook, onCreate, onUnlock, service, setService, hub, setHub }) => {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pax, setPax] = useState(0);
  const [bags, setBags] = useState(0);

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (service) params.service_type = service;
    if (vehicle) params.vehicle_category = vehicle;
    if (pax) params.min_passengers = pax;
    if (bags) params.min_luggage = bags;
    if (hub) params.hub = hub;
    try {
      const { data } = await api.get("/rides", { params });
      setRides(data);
    } finally {
      setLoading(false);
    }
  }, [q, service, vehicle, pax, bags, hub]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    const i = setInterval(load, 10000);
    const onVisible = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearTimeout(t);
      clearInterval(i);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [load, refreshKey, verified]);

  return (
    <section id="live-board" className="scroll-mt-20 pb-16" data-testid="live-board">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-[#10B981]">
            <span className="ff-pulse-dot" /> Live
          </span>
          <h2 className="ff-display mt-1.5 text-3xl sm:text-4xl font-extrabold">{t("Live Exchange Board")}</h2>
        </div>
        <span data-testid="board-count" className="text-sm font-semibold text-[#94A3B8] whitespace-nowrap">{rides.length} {t("corse")}</span>
      </div>
      <BoardFilters q={q} setQ={setQ} service={service} setService={setService} searchRef={searchRef} />
      <div className="mt-3"><HubChips hub={hub} setHub={setHub} /></div>
      <div className="mt-3"><DetailFilters vehicle={vehicle} onVehicle={onVehicle} pax={pax} setPax={setPax} bags={bags} setBags={setBags} /></div>
      {!loading && rides.length === 0 && <EmptyBoard filtered={!!(q.trim() || service || vehicle || pax || bags || hub)} onCreate={onCreate} />}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rides.map((ride, i) => (
          <RideCard key={ride.id} ride={ride} index={i} onBook={onBook} onUnlock={onUnlock} />
        ))}
      </div>
    </section>
  );
};
